<template>
  <main>
    <div class="max-w-1200 hero md:rounded overflow-hidden">
      <div v-if="!isTwoForOneCampaign" class="hero-header-container relative md:mb-10">
        <OImage
          v-bind="menOriginalsImage"
          img-class="hero-header md:rounded-b w-full"
          :alt="$t('Accessibilty.AltTags.ProductImage')"
        />
        <div class="text-neutral-00 absolute text-left left-0 bottom-0 md:top-0 pb-10 px-4 max-w-429 pt-16 md:pt-0 md:pl-9">
          <h1 class="o-heading-1 mb-3 text-left line-height-normal" v-html="$t('LandingsPage.Men.Title', menVariables)" />
          <p class="o-body mb-3 text-left" v-html="$t('LandingsPage.Men.Description', menVariables)" />
          <div class="text-left">
            <NewButton :attribute="{ name: 'data-qa', value: 'signup-desktop' }" tag="nuxt-link" to="/men/signup" icon-right="new-chevron-right">
              {{ isTwoForOneCampaign ? $t('TwoForOneCampaign.TryForFree') : $t('LandingsPage.Men.PreOrderNow', menVariables) }}
            </NewButton>
          </div>
        </div>
      </div>
      <section v-else>
        <TwoForOneHero
          :is-two-for-one-campaign="isTwoForOneCampaign"
          :is-last-chance-two-for-one-campaign="isLastChanceTwoForOneCampaign"
          bg-class="lg:bg-neutral-50"
          :locale="locale"
          :country="country"
          :attribute="{ name: 'data-qa', value: 'signup-desktop' }"
        />
      </section>
      <section class="px-4 py-10 md:flex md:my-10 md:p-0">
        <div class="md:flex-1 md:mr-9 md:pl-9 md:rounded md:bg-neutral-50 md:flex md:flex-col md:justify-center md:items-start md:text-center">
          <h2 class="o-heading-2 text-left md:px-10" v-text="$t('LandingsPage.Men.EveryMonthANewSurprise', menVariables)" />
          <DontWorryUSPS class="my-3 text-left" :usps="heroUSPS" />
          <div class="mb-3 md:px-10">
            <NewButton tag="nuxt-link" color="black" to="/men/signup" icon-right="new-chevron-right">
              {{ $t('LandingsPage.Men.SignupNow', menVariables) }}
            </NewButton>
          </div>
        </div>
        <div class="mt-6 md:flex-1 md:mt-0">
          <VideoComponent
            class="rounded overflow-hidden"
            :video-source="videoSource"
            :placeholder-image="videoPlaceholderImage"
            hide-info-text
            hide-socials
          />
        </div>
      </section>
    </div>
    <div id="how-it-works" />
    <div class="max-w-1200">
      <WhyOta
        ref="howItWorks"
        :country="country"
        class="mt-10"
        :button-text="isTwoForOneCampaign ? $t('TwoForOneCampaign.TryForFree') : $t('TryItNow.IWantThis')"
        :is-women="isWomenCountry"
      />
    </div>
    <div class="bg-neutral-50">
      <div class="max-w-1200">
        <article
          v-for="(feature, i) in features"
          :key="feature.title"
          class="py-10 px-4 md:pt-20 md:pb-0 md:flex"
          :class="{
            'md:flex-row-reverse': i % 2 === 0,
            'md:pb-20': i + 1 === features.length
          }"
        >
          <div
            class="right-flex md:flex md:flex-col md:justify-center"
          >
            <p class="o-heading-3 text-neutral-800 py-5" v-text="feature.title" />
            <p class="o-body pb-5" v-text="feature.description" />
          </div>
          <div
            class="left-flex"
            :class="{ 'md:flex md:justify-end': i % 2 !== 0}"
          >
            <OImage v-bind="feature.image" img-class="rounded" />
          </div>
        </article>
      </div>
    </div>
    <section class="max-w-667">
      <div class="py-10 md:text-center md:py-20">
        <div class="px-4 md:flex md:flex-col md:items-center">
          <h4 class="o-heading-3 mb-3" v-text="$t('LandingsPage.Men.YourLoyaltyProgram.Title', menVariables)" />
          <p class="o-body mb-3" v-text="$t('LandingsPage.Men.YourLoyaltyProgram.Description', menVariables)" />
          <DontWorryUSPS class="mb-3 text-neutral-800" :usps="loyaltyUSPS" />
          <div class="md:text-center">
            <NewButton
              tag="nuxt-link"
              to="/webshop"
              :aria-label="$t('Accessibilty.AriaLabels.GoToWebshop')"
              icon-right="new-chevron-right"
              variant="outline"
              color="black"
            >
              {{ $t('LandingsPage.Men.ExploreMore', menVariables) }}
            </NewButton>
          </div>
        </div>
        <div>
          <div
            ref="loyaltyProductsSlider"
            class="mt-6 flex overflow-x-scroll disable-scrollbars horizontal-scroll pr-6 md:pr-0"
            tabindex="0"
            role="region"
            :aria-label="$t('LandingsPage.Men.YourLoyaltyProgram.Title', menVariables)"
            @keydown.left="scrollLoyaltyProductsLeft"
            @keydown.right="scrollLoyaltyProductsRight"
          >
            <article
              v-for="product in loyaltyProducts"
              :key="product.title"
              class="pl-6 flex-1 horizontal-scroll-center"
            >
              <div class="rounded-t">
                <OImage
                  v-bind="getLoyaltyProductImage(product.i)"
                  img-class="rounded mb-4"
                  :alt="$t('Accessibilty.AltTags.PhotoOfProductWithName', { name: product.title })"
                />
                <p class="text-center o-heading-5 mb-6 text-neutral-800" v-text="product.title" />
                <p class="text-center o-body text-neutral-800" v-text="product.description" />
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
    <div>
      <StarRating
        v-if="rating"
        class="pt-10 px-5 pb-32 md:pt-20 md:pb-40"
        :title="'TryItNow.ReviewsTitle'"
        :rating="rating"
        :reviews="ratingTotal"
        @click="onRatingClick"
      />
    </div>
    <div class="bg-neutral-50">
      <div class="max-w-1200 py-10">
        <Reviews />
        <p class="o-body text-center py-5" v-text="$t('HomePage.SelectedFavoriteReviews')" />
      </div>
    </div>
    <TwoForOneCampaign
      v-if="isTwoForOneCampaign"
      class="py-5 md:py-20"
      :title="$t('TwoForOneCampaign.Title')"
      :link="$t('TwoForOneCampaign.TermsLink')"
      @signup="signupRedirect"
    />
    <div class="py-10 md:py-20">
      <DiscoverMembershipTypes />
    </div>
    <div class="max-w-1200">
      <InstagramWidget hide-texts class="mt-10 md:mt-15" />
    </div>
    <div class="max-w-1200 pb-10 lg:pb-20 md:flex">
      <div class="max-w-600 relative flex md:py-0 md:flex-1 md:pr-0 md:pl-10">
        <div>
          <div class="model-image-side md:py-5 pr-5 md:pr-6 md:flex md:justify-end">
            <OImage
              v-bind="whyOtaImage"
              img-class="rounded"
            />
          </div>
        </div>
        <div class="relative pr-4 md:pt-12">
          <h5 class="o-heading-3 md:whitespace-no-wrap" v-text="$t('LandingsPage.Men.TheseAreThePros')" />
          <p class="new-gray o-body mb-5 mt-2" v-text="$t('LandingsPage.Men.MenPricePerMonth', menVariables)" />
          <USPs class="what-you-get-usps" :usps="prosUSPS" />
          <div class="pt-5">
            <NewButton tag="nuxt-link" to="/men/signup" icon-right="new-chevron-right">
              {{ isTwoForOneCampaign ? $t('TwoForOneCampaign.TryForFree') : $t('LandingsPage.Men.PreOrderNow', menVariables) }}
            </NewButton>
          </div>
        </div>
      </div>
    </div>
    <div class="max-w-56rem my-20 px-3">
      <Faq
        :items="faqItems"
      />
    </div>
    <StickyATC class="md:hidden" :text="$t('HomePage.TryForFreeATC')" @click="signupRedirect" />
  </main>
</template>

<script>
import { addLocalCurrency, convertSrc, getEuros, getImagePaths } from '@/utils/general'
import OImage from '@/components/OImage'
import StarRating from '@/components/StarRating'
import NewButton from '@/components/NewButton'
import VideoComponent from '@/components/VideoComponent'
import InstagramWidget from '@/components/InstagramWidget'
import Reviews from '@/components/Reviews'
import USPs from '@/components/USPs'
import Faq from '@/components/Faq'
import { TrialDuration } from '@/enums/member'
import { mapActions, mapGetters, mapState } from 'vuex'
import DontWorryUSPS from '@/components/Women/Signup/DontWorryUSPS'
import { MenPrices, Orientations, WomenPrices } from '@/enums/products'
import { Currencies } from '@/enums/general'
import { GET_GOOGLE_RATING, GET_FLAGS, SIGNUP_GET_LATEST_DESIGN } from '@/utils/store/actionTypes'
import WhyOta from '@/components/WhyOta'
import TwoForOneHero from '@/components/TwoForOneHero'
import DiscoverMembershipTypes from '@/components/DiscoverMembershipTypes'
import { ROUTER_HASH_UPDATED } from '@/events/eventTypes'
import _ from 'lodash'
import { DeliveryTimes } from '@/enums/shipments'
import StickyATC from '@/components/StickyATC'
import { IS_FLAGS_FETCHED, IS_TWO_FOR_ONE_CAMPAIGN, IS_TWO_FOR_ONE_CAMPAIGN_LAST_DAY } from '@/utils/store/getterTypes'
import TwoForOneCampaign from '@/components/TwoForOneCampaign'

export default {
  name: 'Men',
  layout: 'homepage',
  components: { OImage, StarRating, NewButton, DontWorryUSPS, VideoComponent, Faq, InstagramWidget, Reviews, USPs, WhyOta, DiscoverMembershipTypes, StickyATC, TwoForOneCampaign, TwoForOneHero },
  async fetch () {
    await Promise.all([
      this.getAndSetGoogleRating(),
      this.getAndSetFlags(),
      this.getAndSetLatestDesigns()
    ])
  },
  data () {
    return {
      rating: null,
      ratingTotal: null,
      observer: null,
      latestDesign: {
        [Orientations.menoriginals]: 'gloom'
      }
    }
  },
  computed: {
    isWomenCountry () {
      return false
    },
    menOriginalsImage () {
      return getImagePaths({
        s3: true,
        path: `/images/design/${this.latestDesign[Orientations.menoriginals]}/hero-image/hero-originals`
      })
    },
    heroUSPS () {
      const path = 'LandingsPage.Men.HeroUSPS'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => this.$t(`${path}[${i}]`, { ...this.menVariables }))
    },
    features () {
      const path = 'LandingsPage.Men.Features'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => ({
        image: getImagePaths({
          s3: true,
          path: this.$t(`${path}[${i}].Url`, this.menVariables)
        }),
        title: this.$t(`${path}[${i}].Title`, this.menVariables),
        description: this.$t(`${path}[${i}].Description`, this.menVariables)
      }))
    },
    loyaltyUSPS () {
      const path = 'LandingsPage.Men.YourLoyaltyProgram.USPS'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => this.$t(`${path}[${i}]`, { ...this.menVariables }))
    },
    prosUSPS () {
      const path = 'LandingsPage.Men.ProsUSPS'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => this.$t(`${path}[${i}]`, { ...this.menVariables }))
    },
    faqItems () {
      const path = 'LandingsPage.Men.FAQ'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => ({
        title: this.$t(`${path}[${i}].title`, this.menVariables),
        description: this.$t(`${path}[${i}].description`, this.menVariables)
      }))
    },
    videoSource () {
      return convertSrc(`/images/frontend/static/try-now-${this.locale}.mp4`, true)
    },
    videoPlaceholderImage () {
      return convertSrc('/images/frontend/static/try-now.webp', true)
    },
    whyOtaImage () {
      return getImagePaths({
        path: '/images/frontend/static/men-originals-model',
        s3: true
      })
    },
    loyaltyProducts () {
      const path = 'LandingsPage.Men.YourLoyaltyProgram.Items'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, i) => ({
        i: i + 1,
        title: this.$t(`${path}[${i}].Title`, this.menVariables),
        description: this.$t(`${path}[${i}].Description`, this.menVariables)
      }))
    },
    menVariables () {
      const countryCode = `${this.locale}-${this.country}`

      return {
        trialDuration: TrialDuration[this.country],
        trialDays: TrialDuration[this.country],
        loyaltyAmount: getEuros(this.country, 10),
        womenPrice: addLocalCurrency(WomenPrices[this.country], countryCode, Currencies[this.country]),
        menPrice: addLocalCurrency(MenPrices[this.country], countryCode, Currencies[this.country]),
        freePrice: addLocalCurrency(0, countryCode, Currencies[this.country]),
        earliest: DeliveryTimes[this.country].earliest,
        latest: DeliveryTimes[this.country].latest
      }
    },
    ...mapState('app', {
      country: state => state.country,
      locale: state => state.locale
    }),
    ...mapGetters('flags', {
      isFlagsFetched: IS_FLAGS_FETCHED,
      isTwoForOneCampaign: IS_TWO_FOR_ONE_CAMPAIGN,
      isLastChanceTwoForOneCampaign: IS_TWO_FOR_ONE_CAMPAIGN_LAST_DAY
    })
  },
  created () {
    if (process.client) {
      this.observer = new IntersectionObserver(
        this.onElementObserved,
        {
          root: this.$el,
          threshold: 0.8,
          rootMargin: '0px 0px -50px 0px'
        }
      )
      this.$registerEventListener(ROUTER_HASH_UPDATED.event, this.pageScrollByHash)
    }
  },
  mounted () {
    this.pageScrollByHash()
  },
  beforeDestroy () {
    this.$unregisterEventListener(ROUTER_HASH_UPDATED.event, this.pageScrollByHash)
  },
  methods: {
    onElementObserved (entries) {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) {
          return
        }

        this.observer.unobserve(target)

        this.open = true
      })
    },
    pageScroll (refName, retries = 0) {
      const elem = this.$refs[refName]?.$el
      if (retries > 10) {
        return
      }

      if (!elem || !elem.scrollIntoView || this.loading) {
        setTimeout(() => {
          this.pageScroll(refName, this.loading ? retries + 1 : 0)
        }, 200)
        return
      }

      elem.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
    signupRedirect () {
      this.$router.push(this.$i18n.localePath({ name: 'men-signup' }))
    },
    pageScrollByHash () {
      if (this.$route.hash) {
        setTimeout(() => {
          this.pageScroll(_.camelCase(this.$route.hash.substring(1)))
        }, 1500)
      }
    },
    onRatingClick () {
      window.open(this.$t('HomePage.GoogleReviewPage'), '_blank', 'noopener')
    },
    getLoyaltyProductImage (i = 1) {
      return getImagePaths({
        s3: true,
        path: `/images/frontend/static/loyalty-men-${i}`
      })
    },
    async getAndSetGoogleRating () {
      try {
        const { rating, ratingTotal } = await this.getGoogleRating()

        this.rating = rating
        this.ratingTotal = ratingTotal
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    async getAndSetFlags () {
      if (this.isFlagsFetched) {
        return
      }

      try {
        await this.getFlags()
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    async getAndSetLatestDesigns () {
      try {
        const designs = await this.getLatestDesign({ women: false, men: true, boys: false })

        const menOriginalsDesign = designs.find(design => design.productGroups.find(productGroup => productGroup.reference === Orientations.menoriginals))

        if (menOriginalsDesign) {
          this.latestDesign[Orientations.menoriginals] = menOriginalsDesign.slug
        }
      } catch (error) {
        this.$ota.general.logError(error)
      }
    },
    ...mapActions('homepage', {
      getGoogleRating: GET_GOOGLE_RATING
    }),
    ...mapActions('flags', {
      getFlags: GET_FLAGS
    }),
    ...mapActions('signup', {
      getLatestDesign: SIGNUP_GET_LATEST_DESIGN
    }),
    scrollLoyaltyProductsLeft () {
      if (this.$refs.loyaltyProductsSlider) {
        this.$refs.loyaltyProductsSlider.scrollLeft -= 400 // Adjust scroll amount as needed
      }
    },
    scrollLoyaltyProductsRight () {
      if (this.$refs.loyaltyProductsSlider) {
        this.$refs.loyaltyProductsSlider.scrollLeft += 400 // Adjust scroll amount as needed
      }
    }
  }
}
</script>

<style scoped lang="postcss">
@screen xsplus {
  .oimage >>> .hero-header {
    @apply bg-women-fill;
    min-height: 100%;
  }
}

.model-image-side .oimage >>> img {
  max-width: unset;
}

.max-w-1200 {
  @apply mx-auto;
  max-width: 119.2rem;
}

.max-w-600 {
  @apply mx-auto;
  max-width: 60rem;
}

.new-gray {
  color: #4B5563;
}

.max-w-800 {
  @apply mx-auto;
  max-width: 86rem;
}

.bluebg {
  background-color: #200238;
}

.max-w-667 {
  @apply mx-auto;
  max-width: 86.7rem;
}

.horizontal-scroll-center {
  min-width: 21.2rem;
}

.dont-worry-usps {
  @apply bg-transparent p-0;
}

>>> .max-w-51rem {
  max-width: unset;
}

.line-height-normal {
  line-height: normal;
}

.what-you-get-usps {
  @apply px-0 !important;

  >>> .ota-usps-container {
    @apply p-0 bg-transparent !important;

    svg {
      @apply w-4 h-4 fill-green-400;
    }

    &-grid {
      @apply block;
      max-width: 32rem;
    }
  }
}

@screen md {
    .bluebg {
      background-color: transparent;
    }

    .oimage >>> .hero-header {
      min-height: unset;
    }

    .dont-worry-usps {
      @apply px-10;
    }

   .max-w-429 {
    @apply m-auto bottom-0 flex flex-col justify-center text-center;
    max-width: 42.9rem;
  }

  .hero-header-container {
    max-width: unset;
  }

  .max-w-56rem {
    @apply mx-auto;
    max-width: 56.4rem;
  }

  .left-flex {
    flex: 60;
  }

  .right-flex {
    @apply px-10;
    flex: 40;
  }
}

@screen lg {
  .left-flex {
    flex: 70;
  }

  .right-flex {
    flex: 30;
  }

  .oimage >>> .hero-header {
    min-height: 60rem;
  }

  >>> .ota-usps-container {
    @apply bg-neutral-50 !important;
  }
}

/* Add visual indication of focus */
.horizontal-scroll:focus {
  outline: 2px solid #36C0B6; /* Use your primary color */
  outline-offset: -2px;
}

/* Add scrollbar styling for visual users */
.horizontal-scroll {
  scroll-behavior: smooth; /* For smooth scrolling */
}
</style>
